import React, { useState, useRef, useEffect, useContext } from "react";
import { v4 as uuidv4 } from "uuid";
import { Helmet } from "react-helmet";
import { AuthContext } from "../../context/AuthContext";
import FullPageLoader from "../../components/FullPageLoader";
import { useNavigate, useParams } from "react-router-dom";
import useAxios from "../../hooks/useAxios";
import { apiEndPoint } from "../../constants/api.constans";
import useGetDataWithCache from "../../hooks/useGetDataWithCache";
import { useRemoteConfig } from "../../hooks/useRemoteConfig";

// Define the types for the props
interface PromptTextEntryProps {
  data: { title?: string; id?: string; placeholder: string };
  onChange: (data: { title: string; id: string }) => void;
  deleteUserEntry: () => void;
}

const PromptTextEntry: React.FC<PromptTextEntryProps> = ({ data = {}, onChange, deleteUserEntry }) => {
  const { title = "", id = "", placeholder = "" } = data;
  const [value, setValue] = useState<string>(title);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const { config } = useRemoteConfig();

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onChange({ title: value, id });
    }, 1000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value]);

  return (
    <div className="flex justify-center items-center mb-4">
      <div className="collapse bg-base-200">
        <input type="checkbox" />
        <div className="collapse-title text-xl font-medium">{title || config.locale_promt_user_input_no_name_default_title}</div>
        <div className="collapse-content flex items-center space-x-2">
          <input onChange={(e) => setValue(e.target.value)} value={value} className="input input-bordered input-primary w-full" placeholder={placeholder} />
        </div>
      </div>
      <button className="btn btn-circle ml-4" onClick={() => deleteUserEntry(id)}>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

interface TextEntryProps {
  data: { value?: string; id: string; placeholder?: string };
  onChange: (data: { value: string; id: string }) => void;
}

const TextEntry: React.FC<TextEntryProps> = ({ data, onChange, deleteText, index }) => {
  const [value, setValue] = useState<string>(data.value || "");
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onChange({ value, id: data.id });
    }, 1000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value]);

  return (
    <div className="flex justify-center items-center mb-4">
      <textarea key={data.id} className="textarea textarea-bordered w-full" value={value} onChange={(e) => setValue(e.target.value)} rows={4} placeholder={data.placeholder} />
      {!!index && (
        <button className="btn btn-circle ml-4" onClick={() => deleteText(data.id)}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
};

interface TemplateItem {
  id: string;
  type: "text" | "entry";
  value?: string;
  title?: string;
  placeholder?: string;
}

interface TemplateEditProps {
  template: { metadata: { name: string; id: string; description: string }; data: TemplateItem[] };
  setTempalte: React.Dispatch<React.SetStateAction<{ metadata: { name: string }; data: TemplateItem[] }>>;
}

const TemplateEdit: React.FC<TemplateEditProps> = ({ template, setTempalte }) => {
  const [metaData, setMetadata] = useState(template.metadata);
  const [templateData, setTemplateData] = useState<TemplateItem[]>(template.data);
  const { config } = useRemoteConfig();

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setTempalte({ metadata: metaData, data: templateData });
    }, 100);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaData, templateData]);

  const addNewTextArea = () => {
    setTemplateData((prev) => [...prev, { id: uuidv4(), type: "text", value: "", placeholder: config.locale_continue_prompt_placeholder }]);
  };

  const addNewUserEntry = () => {
    setTemplateData((prev) => [...prev, { id: uuidv4(), type: "entry", title: "", placeholder: config.locale_prompt_iser_input_placeholder }]);
  };

  const updateUserEntry = ({ title, id }: { title: string; id: string }) => {
    setTemplateData((prev) => prev.map((item) => (item.id === id ? { ...item, title } : item)));
  };

  const updateText = ({ value, id }: { value: string; id: string }) => {
    setTemplateData((prev) => prev.map((item) => (item.id === id ? { ...item, value } : item)));
  };

  const deleteText = (id: string) => {
    setTemplateData((prev) => prev.filter((item) => item.id !== id));
  };

  // Function to delete a user entry based on its ID
  const deleteUserEntry = (id: string) => {
    setTemplateData((prev) => prev.filter((item) => item.id !== id));
  };

  const lastEntryType = templateData?.[templateData?.length - 1]?.type;

  return (
    <div>
      <Helmet>
        <title>{`${config.helmet_title_edit_genie}: ${metaData.name} - ${config.appname}`}</title>
      </Helmet>
      <label className="input input-bordered flex items-center gap-2 mb-4 r">
        {config.locale_add_new_genie_name_input_name}
        <input
          type="text"
          className="grow"
          placeholder={config.locale_add_new_genie_name_input_placeholder}
          value={metaData.name}
          onChange={(event) => setMetadata((md) => ({ ...md, name: event.target.value }))}
        />
      </label>
      <label className="input input-bordered flex items-center gap-2 mb-8 h-[auto]">
        {config.locale_add_new_genie_description_input_name}
        <textarea
          className="textarea textarea-ghost grow mt-2 mb-2 outline-none border-none focus:outline-none focus:border-none focus:ring-none"
          rows={2}
          type="text"
          // className="grow"
          placeholder={config.locale_add_new_genie_description_input_placeholder}
          value={metaData.description}
          onChange={(event) => setMetadata((md) => ({ ...md, description: event.target.value }))}
        />
      </label>
      <br />

      <div className="divider divider-neutral">{config.locale_create_genie_ai_prompt_divider}</div>

      {templateData.map((t, index) =>
        t.type === "text" ? (
          <TextEntry deleteText={deleteText} key={t.id} data={t} onChange={updateText} index={index} />
        ) : (
          <PromptTextEntry deleteUserEntry={deleteUserEntry} key={t.id} data={t} onChange={updateUserEntry} index={index} />
        )
      )}
      {/* <div className="flex flex-col md:flex-row md:justify-between mt-4 mb-8"> */}
      <div className="flex flex-col md:flex-row md:justify-between mt-4 mb-8 gap-4">
        <button className="btn w-full md:w-48 btn-outline" onClick={addNewUserEntry}>
          {config.locale_add_new_user_input_button}
        </button>

        <button disabled={lastEntryType === "text"} className="btn btn-primary mb-2 md:mb-0 w-full md:w-48 btn-outline" onClick={addNewTextArea}>
          {config.locale_add_new_text_prompt_input_button}
        </button>
      </div>
    </div>
  );
};

const EditTemplate: React.FC = () => {
  const { templateId } = useParams();
  const { getData, data } = useGetDataWithCache("templateDetails");
  const [template, setTempalte] = useState();
  const dispatch = useContext(AuthContext).dispatch;
  const { config } = useRemoteConfig();

  if (!template && templateId && !data) {
    getData({ id: templateId });
  }

  useEffect(() => {
    setTempalte(data);
  }, [data]);

  const { put } = useAxios();
  const navigate = useNavigate();

  const handleEdit = () => {
    dispatch({ type: "LOADING_START", payload: "updateGenie" });

    put(apiEndPoint + "/updateTemplateApi", template)
      .then((res) => {
        getData({ id: templateId }, true)
          .then(() => {
            navigate(`/genie/${res?.data?.metadata?.id}`);
          })
          .finally(() => {
            dispatch({ type: "LOADING_END", payload: "updateGenie" });
            dispatch({ type: "SET_TOAST", payload: "updateGenieSuccess" });
          });
      })
      .catch(() => {
        dispatch({ type: "SET_TOAST", payload: "updateGenieFailed" });

        dispatch({ type: "LOADING_END", payload: "updateGenie" });
      });
  };

  if (!template) return <FullPageLoader />;

  return (
    <div className="flex flex-col w-full max-w-[95vw] md:max-w-[50vw]">
      <h3 className="text-2xl font-bold mb-4 text-center mb-8">{config.locale_edit_template_update_title}</h3>
      <TemplateEdit template={template} setTempalte={setTempalte} />
      <div className="flex mt-4">
        <button className="btn btn-accent flex-grow" onClick={handleEdit}>
          {config.locale_edit_template_update_button}
        </button>
        {/* <select className="select select-primary w-auto ml-4" onChange={(e) => setPublicOrPrivate(e.target.value)}>
          <option selected={publicOrPrivate === "Public"}>Public</option>
          <option selected={publicOrPrivate === "Private"}>Private</option>
        </select> */}
      </div>
    </div>
  );
};

export default EditTemplate;
